/**
 * screens/compliance/compliance.tsx
 * Compliance · DPDP — wired to /api/hire/v2/compliance/*.
 *
 * Premium rebuild:
 *  · Progressive, non-blocking load — the page shell + skeleton shimmer paint
 *    instantly; each section (stats / sweep / stream / ledger) fills the moment
 *    its own request resolves, so a slow endpoint never blocks the whole screen.
 *  · Staggered entrance (sk-rise), count-up KPIs, hover-lift cards, a live
 *    pulsing cron dot, and a connective timeline for the consent event stream.
 *  · Pure grayscale + IBM Plex, honouring the SK Hire design system. All motion
 *    is web-only and respects prefers-reduced-motion (see web/index.html).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ScrollView, Platform, StyleSheet } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';

import { useTheme } from '../../context/themeContext';
import { useBreakpoints, ContentMaxWidth } from '../../theme/responsive';
import { StatusChip, Panel, ActionButton, Icon } from '../../components/ui';
import { MiniAvatar } from '../../components/hire/badges';
import { useHover } from '../../hooks/useHover';
import { web } from '../../utils/web';
import { toneColors, type Tone } from '../../theme/tone';
import {
  getConsentStats, getExpiring, getConsentEvents, getErasures,
  type ConsentStats, type ExpiringItem, type ConsentEventItem, type ErasureItem,
} from '../../services/hire/complianceV2';
import { recordConsent } from '../../services/hire/candidateV2';

const EVENT_TONE: Record<string, Tone> = { granted: 'success', renewed: 'success', withdrawn: 'danger', expired: 'warn' };
const isWebPlatform = Platform.OS === 'web';

/* ── premium motion helpers ───────────────────────────────────────────── */

/** CSS entrance (staggered fade + rise) — see @keyframes sk-rise in index.html. */
function rise(delay = 0): object {
  return web({ animation: 'sk-rise 460ms cubic-bezier(.2,.8,.2,1) both', animationDelay: `${delay}ms` });
}

/** Animate a number from 0 → target on mount (web only). */
function useCountUp(target: number, duration = 720): number {
  const [n, setN] = useState(isWebPlatform ? 0 : target);
  useEffect(() => {
    if (!isWebPlatform) { setN(target); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
}

/* ── skeleton shimmer ─────────────────────────────────────────────────── */

function Skeleton({ w, h, r = 6, style }: { w?: number | string; h: number; r?: number; style?: object }) {
  const { theme } = useTheme();
  const { colors } = theme;
  return (
    <View
      style={[
        { width: (w as number) ?? '100%', height: h, borderRadius: r, backgroundColor: colors.paper2 },
        web({
          backgroundImage: `linear-gradient(90deg, ${colors.paper2} 0%, ${colors.paper3} 50%, ${colors.paper2} 100%)`,
          backgroundSize: '300% 100%',
          animation: 'sk-shimmer 1.4s ease-in-out infinite',
        }),
        style,
      ]}
    />
  );
}

/* ── premium KPI card (count-up + tone accent + hover-lift) ───────────── */

function StatCard({ label, value, tone, delay, loading }: {
  label: string; value: number; tone: Tone; delay: number; loading: boolean;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, spacing } = theme;
  const { hovered, hoverProps } = useHover();
  const c = toneColors(colors, tone);
  const n = useCountUp(loading ? 0 : value);

  return (
    <View
      {...hoverProps}
      style={[
        {
          flex: 1,
          minWidth: 150,
          borderRadius: radii.lg,
          backgroundColor: colors.card,
          borderWidth: 1,
          borderColor: colors.line,
          overflow: 'hidden',
        },
        rise(delay),
        web({
          boxShadow: '0 1px 0 rgba(16,24,40,0.04)',
          transition: 'transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 180ms ease, border-color 180ms ease',
        }),
        hovered && web({
          transform: 'translateY(-3px)',
          boxShadow: '0 14px 30px -18px rgba(16,24,40,0.30)',
          borderColor: colors.lineStrong,
        }),
      ]}
    >
      {/* tone accent rail (grayscale lightness carries meaning) */}
      <View style={{ height: 3, backgroundColor: c.fg, opacity: hovered ? 1 : 0.65, ...web({ transition: 'opacity 180ms ease' }) }} />
      <View style={{ padding: spacing[4] }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: spacing[2] }}>
          <View style={{ width: 7, height: 7, borderRadius: radii.pill, backgroundColor: c.fg }} />
          <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontWeight: fontWeight.medium }}>{label}</Text>
        </View>
        {loading ? (
          <Skeleton w={54} h={30} r={8} style={{ marginTop: 2 }} />
        ) : (
          <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.5, fontVariant: ['tabular-nums'] }}>
            {n}
          </Text>
        )}
      </View>
    </View>
  );
}

/* ── live pulsing dot (cron / append-only "live" tells) ───────────────── */

function PulseDot({ color }: { color: string }) {
  return (
    <View style={{ width: 7, height: 7, alignItems: 'center', justifyContent: 'center' }}>
      {isWebPlatform && (
        <View style={[
          { position: 'absolute', width: 7, height: 7, borderRadius: 999, backgroundColor: color },
          web({ animation: 'sk-pulse 2s cubic-bezier(.2,.8,.2,1) infinite' }),
        ]} />
      )}
      <View style={{ width: 7, height: 7, borderRadius: 999, backgroundColor: color }} />
    </View>
  );
}

/* ── hover-highlight row wrapper ──────────────────────────────────────── */

function Row({ children, last, delay }: { children: React.ReactNode; last: boolean; delay: number }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { hovered, hoverProps } = useHover();
  return (
    <View
      {...hoverProps}
      style={[
        {
          marginHorizontal: -8,
          paddingHorizontal: 8,
          borderRadius: 8,
          borderBottomWidth: last ? 0 : 1,
          borderBottomColor: colors.line,
        },
        rise(delay),
        web({ transition: 'background-color 140ms ease' }),
        hovered && { backgroundColor: colors.paper2, borderBottomColor: 'transparent' },
      ]}
    >
      {children}
    </View>
  );
}

/* ── consent event-stream timeline row ────────────────────────────────── */

function EventRow({ name, eventType, date, tone, last, delay }: {
  name: string; eventType: string; date: string; tone: Tone; last: boolean; delay: number;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  const { hovered, hoverProps } = useHover();
  const c = toneColors(colors, tone);
  return (
    <View
      {...hoverProps}
      style={[
        { flexDirection: 'row', gap: 12, marginHorizontal: -8, paddingHorizontal: 8, borderRadius: 10, backgroundColor: hovered ? colors.paper2 : 'transparent' },
        web({ transition: 'background-color 140ms ease' }),
        rise(delay),
      ]}
    >
      {/* timeline gutter: tone dot (soft ring) + connective line */}
      <View style={{ alignItems: 'center', width: 12 }}>
        <View style={{ width: 10, height: 10, borderRadius: 999, marginTop: 13, backgroundColor: c.fg, borderWidth: 2, borderColor: colors.card, ...(isWebPlatform ? ({ boxShadow: `0 0 0 3px ${c.bg}` } as object) : {}) }} />
        {!last && <View style={{ flex: 1, width: 1.5, backgroundColor: colors.line, marginVertical: 2 }} />}
      </View>
      <View style={{ flex: 1, minWidth: 0, paddingTop: 9, paddingBottom: last ? 9 : 16 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Text numberOfLines={1} style={{ flexShrink: 1, fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.semibold }}>{name}</Text>
          <View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, backgroundColor: c.bg, borderWidth: 1, borderColor: c.border }}>
            <Text style={{ fontSize: 10, fontWeight: fontWeight.semibold, color: c.fg, textTransform: 'capitalize' }}>{eventType}</Text>
          </View>
        </View>
        <Text style={{ fontSize: fontSize.xs, color: colors.ink4, marginTop: 4 }}>{date}</Text>
      </View>
    </View>
  );
}

export default function ComplianceScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { isMobile } = useBreakpoints();

  const [stats, setStats] = useState<ConsentStats | null>(null);
  const [expiring, setExpiring] = useState<ExpiringItem[] | null>(null);
  const [events, setEvents] = useState<ConsentEventItem[] | null>(null);
  const [erasures, setErasures] = useState<ErasureItem[] | null>(null);
  const [renewing, setRenewing] = useState<string | null>(null);

  // Progressive load: fire every request independently and paint each section
  // the instant it resolves — a slow endpoint never blocks the rest.
  const load = useCallback(() => {
    let active = true;
    getConsentStats().then(s => { if (active) setStats(s); }).catch(() => { if (active) setStats({ active: 0, expiring: 0, expired: 0, erased: 0 }); });
    getExpiring().then(x => { if (active) setExpiring(x); }).catch(() => { if (active) setExpiring([]); });
    getConsentEvents().then(e => { if (active) setEvents(e); }).catch(() => { if (active) setEvents([]); });
    getErasures().then(r => { if (active) setErasures(r); }).catch(() => { if (active) setErasures([]); });
    return () => { active = false; };
  }, []);
  useFocusEffect(load);

  const renew = async (candidateId: string) => {
    setRenewing(candidateId);
    try { await recordConsent(candidateId, 'renew'); load(); } finally { setRenewing(null); }
  };

  const fmt = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : 32, paddingBottom: 60 }}>
      <View style={{ width: '100%', maxWidth: ContentMaxWidth.default, alignSelf: 'center', gap: 16 }}>

        {/* Header */}
        <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 14 }, rise(0)]}>
          <View style={{
            width: 44, height: 44, borderRadius: radii.md, backgroundColor: colors.accent,
            alignItems: 'center', justifyContent: 'center',
            ...web({ boxShadow: '0 8px 20px -10px rgba(16,24,40,0.45)' }),
          }}>
            <Icon name="shield" size={22} color={colors.onAccent} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontSize: fontSize['2xl'], fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.4 }}>Compliance · DPDP</Text>
          </View>
        </View>

        {/* KPI grid */}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
          <StatCard label="Active consents" value={stats?.active ?? 0}   tone="success" delay={60}  loading={!stats} />
          <StatCard label="Expiring ≤14d"   value={stats?.expiring ?? 0} tone="warn"    delay={120} loading={!stats} />
          <StatCard label="Expired"          value={stats?.expired ?? 0}  tone="danger"  delay={180} loading={!stats} />
          <StatCard label="Erased"           value={stats?.erased ?? 0}   tone="neutral" delay={240} loading={!stats} />
        </View>

        <View style={[{ flexDirection: 'row', gap: isMobile ? 16 : 12, alignItems: 'flex-start' }, isMobile && { flexDirection: 'column' }]}>
          {/* LEFT column — expiry sweep + erasure ledger stacked, so the audit
              fills the space beside the taller event stream instead of sitting
              stranded full-width below. */}
          <View style={[isMobile ? { width: '100%' } : { flex: 1 }, { gap: isMobile ? 16 : 12 }]}>
            <View style={rise(300)}>
              <Panel title="Consent expiry sweep" action={
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, paddingHorizontal: 9, paddingVertical: 3, borderRadius: radii.pill, backgroundColor: colors.warnSoft, borderWidth: 1, borderColor: colors.warnLine }}>
                  <PulseDot color={colors.warn} />
                  <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.warn }}>cron</Text>
                </View>
              }>
                {expiring === null ? (
                  <View style={{ gap: 14, paddingVertical: 4 }}>
                    {[0, 1].map(i => (
                      <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <Skeleton w={28} h={28} r={999} />
                        <View style={{ flex: 1, gap: 6 }}><Skeleton w={'55%'} h={12} /><Skeleton w={'80%'} h={10} /></View>
                        <Skeleton w={58} h={28} r={8} />
                      </View>
                    ))}
                  </View>
                ) : expiring.length === 0 ? (
                  <Text style={[styles.empty, { color: colors.ink3 }]}>No consent expiring in 14 days.</Text>
                ) : (
                  expiring.map((e, i) => (
                    <Row key={e.candidateId} last={i === expiring.length - 1} delay={i * 45}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9 }}>
                        <MiniAvatar name={e.name} size={28} />
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <Text numberOfLines={1} style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.medium }}>{e.name}</Text>
                          <Text style={{ fontSize: fontSize.xs, color: colors.ink3 }}>{e.purpose} · TTL {fmt(e.ttlExpiresAt)}</Text>
                        </View>
                        <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: e.daysLeft <= 7 ? colors.warn : colors.ink3 }}>{e.daysLeft}d</Text>
                        <ActionButton label="Renew" variant="success" size="sm" onPress={() => renew(e.candidateId)} loading={renewing === e.candidateId} />
                      </View>
                    </Row>
                  ))
                )}
              </Panel>
            </View>

            {/* Erasure ledger — moved up into the left column to fill the space. */}
            <View style={rise(420)}>
              <Panel title="Erasure ledger" action={<StatusChip tone="danger" label="hire_erasure_events" dot={false} />}>
                {erasures === null ? (
                  <View style={{ gap: 14, paddingVertical: 4 }}>
                    {[0].map(i => (
                      <View key={i} style={{ gap: 6 }}><Skeleton w={'25%'} h={13} /><Skeleton w={'70%'} h={10} /></View>
                    ))}
                  </View>
                ) : erasures.length === 0 ? (
                  <Text style={[styles.empty, { color: colors.ink3 }]}>No erasures recorded — the sanctioned destructive path leaves an audit here.</Text>
                ) : (
                  erasures.map((e, i) => (
                    <Row key={e.id} last={i === erasures.length - 1} delay={i * 45}>
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12, paddingVertical: 9 }}>
                        <View style={{ minWidth: 0, flex: 1 }}>
                          <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.medium }}>{e.name}</Text>
                          <Text style={{ fontSize: fontSize.xs, color: colors.ink3, marginTop: 1 }}>key destroyed · {e.vectorsNulled} vectors + {e.summariesNulled} summaries nulled{e.emailHashNulled ? ' · email hash nulled' : ''}</Text>
                        </View>
                        <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink4 }}>{fmt(e.recordedAt)}</Text>
                      </View>
                    </Row>
                  ))
                )}
              </Panel>
            </View>
          </View>

          {/* RIGHT column — Consent event stream timeline */}
          <View style={[isMobile ? { width: '100%' } : { flex: 1 }, rise(360)]}>
            <Panel title="Consent event stream" action={<StatusChip tone="neutral" label="append-only" dot={false} />}>
              {events === null ? (
                <View style={{ gap: 14, paddingVertical: 4 }}>
                  {[0, 1, 2].map(i => (
                    <View key={i} style={{ flexDirection: 'row', gap: 10 }}>
                      <Skeleton w={7} h={7} r={999} style={{ marginTop: 4 }} />
                      <View style={{ flex: 1, gap: 6 }}><Skeleton w={'60%'} h={12} /><Skeleton w={'35%'} h={10} /></View>
                    </View>
                  ))}
                </View>
              ) : events.length === 0 ? (
                <Text style={[styles.empty, { color: colors.ink3 }]}>No consent events yet.</Text>
              ) : (
                events.map((e, i) => (
                  <EventRow
                    key={e.id}
                    name={e.name}
                    eventType={e.eventType}
                    date={fmt(e.recordedAt)}
                    tone={EVENT_TONE[e.eventType] ?? 'neutral'}
                    last={i === events.length - 1}
                    delay={i * 40}
                  />
                ))
              )}
            </Panel>
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  empty: { paddingVertical: 18, textAlign: 'center', fontSize: 12, color: '#9E9E9E' },
});
